import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loading } from "@/components/bi/Loading";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Activity, Database, Timer, HardDrive, Eye, LayoutDashboard, ShieldCheck } from "lucide-react";

export const fmtBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
};

const num = (n) => Number(n || 0).toLocaleString();

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="glass-card border-0 p-4 flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-[color:rgba(43,78,255,0.10)] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[color:var(--brand-ocean)]" /></div>
    <div className="min-w-0">
      <div data-testid={testid} className="text-xl font-bold text-[color:var(--ink)] truncate">{value}</div>
      <div className="text-[11px] text-[color:var(--ink-3)] truncate">{label}</div>
      {sub && <div className="text-[10px] text-[color:var(--ink-3)] opacity-80 truncate">{sub}</div>}
    </div>
  </Card>
);

const Panel = ({ title, hint, children, testid }) => (
  <Card className="glass-card border-0 p-4" data-testid={testid}>
    <div className="flex items-baseline justify-between mb-2">
      <div className="text-[11px] font-semibold text-[color:var(--ink-2)] uppercase tracking-wide">{title}</div>
      {hint && <div className="text-[10px] text-[color:var(--ink-3)]">{hint}</div>}
    </div>
    {children}
  </Card>
);

const Bar = ({ value, max }) => (
  <div className="h-1.5 rounded-full bg-[color:var(--surface-2)] overflow-hidden mt-1">
    <div className="h-full rounded-full bg-[color:var(--brand-ocean)]" style={{ width: `${Math.max(3, (value / Math.max(1, max)) * 100)}%` }} />
  </div>
);

// Item: a tenant admin's own usage page — storage, query volume, busiest dashboards.
export function AdminUsage() {
  const nav = useNavigate();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/usage/tenant?days=${days}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [days]);
  if (!data) return <Loading label="Loading usage" />;
  if (data.error) return <div className="text-sm text-[color:var(--ink-3)]">Failed to load usage.</div>;

  const act = data.activity || {}, views = data.views || {}, q = data.queries || {}, st = data.storage || {};
  const busiest = views.busiest_dashboards || [];
  const maxViews = Math.max(...busiest.map((b) => b.views), 1);
  const day = act.by_day || [];
  const maxDay = Math.max(...day.map((d) => d.count), 1);

  return (
    <div className="space-y-4" data-testid="admin-usage-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg text-[color:var(--ink)]">Usage &amp; storage</h3>
          <p className="text-[11px] text-[color:var(--ink-3)]">{data.tenant?.name} · {num(data.counts?.users)} people · {num(data.counts?.dashboards)} dashboards</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="usage-days" className="h-8 w-[140px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>{[["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={HardDrive} label="Storage used" value={fmtBytes(st.total_bytes)} testid="usage-storage"
          sub={`${num(st.datasets)} datasets · ${num(st.rows)} rows${st.is_estimate ? " (estimated)" : ""}`} />
        <Kpi icon={Database} label={`Queries · last ${q.window_days}d`} value={num(q.count)} testid="usage-queries"
          sub={`${q.avg_ms} ms average · ${q.cache_hit_rate}% cached`} />
        <Kpi icon={Eye} label="Dashboard opens" value={num(views.total)} testid="usage-views"
          sub={`${num(data.counts?.published)} published dashboards`} />
        <Kpi icon={Users} label="Active people" value={num(act.active_users)} testid="usage-active-users"
          sub={`${num(act.actions)} actions recorded`} />
      </div>

      <Panel title="Busiest dashboards" hint={`last ${data.days} days`} testid="usage-busiest">
        {busiest.length ? (
          <div className="space-y-2">
            {busiest.map((b) => (
              <button key={b.dashboard_id} data-testid={`usage-dash-${b.dashboard_id}`}
                onClick={() => nav(`/builder/${b.dashboard_id}`)}
                className="w-full text-left group">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-[color:var(--ink)] truncate group-hover:text-[color:var(--brand-ocean)]">{b.name}</span>
                  <span className="text-[color:var(--ink-3)] whitespace-nowrap">
                    {num(b.views)} opens · {num(b.viewers)} {b.viewers === 1 ? "person" : "people"}
                    {b.public_views ? ` · ${num(b.public_views)} shared` : ""}
                  </span>
                </div>
                <Bar value={b.views} max={maxViews} />
              </button>
            ))}
          </div>
        ) : <p className="text-xs text-[color:var(--ink-3)]">No dashboard was opened in this window yet.</p>}
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Panel title="Activity per day" testid="usage-by-day">
          <div className="flex items-end gap-[3px] h-24">
            {day.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 rounded-t min-w-[3px] max-w-[18px]"
                style={{ height: `${Math.max(4, (d.count / maxDay) * 100)}%`, backgroundColor: "var(--brand-ocean)", opacity: 0.7 }} />
            ))}
            {!day.length && <div className="text-xs text-[color:var(--ink-3)]">No activity in this window.</div>}
          </div>
          {!!day.length && (
            <div className="flex justify-between text-[10px] text-[color:var(--ink-3)] mt-1.5">
              <span>{day[0].date}</span>
              {day.length > 1 && <span>{day[day.length - 1].date}</span>}
            </div>
          )}
        </Panel>

        <Panel title="Most active people" testid="usage-top-users">
          <div className="space-y-1">
            {(act.top_users || []).slice(0, 6).map((u) => (
              <div key={u.email} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-[color:var(--ink-2)] flex items-center gap-1.5">
                  {u.staff && <ShieldCheck className="w-3 h-3 text-[color:var(--brand-ocean)] shrink-0" title="Eazy staff" />}
                  {u.email}
                </span>
                <span className="text-[color:var(--ink-3)]">{num(u.actions)}</span>
              </div>
            ))}
            {!(act.top_users || []).length && <p className="text-xs text-[color:var(--ink-3)]">Nobody has acted in this window.</p>}
          </div>
        </Panel>

        <Panel title="Heaviest datasets" hint={`queries · last ${q.window_days}d`} testid="usage-busiest-datasets">
          <div className="space-y-1">
            {(q.busiest_datasets || []).slice(0, 6).map((d) => (
              <div key={d.dataset_id} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-[color:var(--ink-2)]">{d.name}</span>
                <span className="text-[color:var(--ink-3)] whitespace-nowrap">{num(d.queries)} · {d.avg_ms} ms</span>
              </div>
            ))}
            {!(q.busiest_datasets || []).length && <p className="text-xs text-[color:var(--ink-3)]">No queries ran in this window.</p>}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={LayoutDashboard} label="Dashboards" value={num(data.counts?.dashboards)} testid="usage-count-dashboards" />
        <Kpi icon={Database} label="Datasets" value={num(data.counts?.datasets)} testid="usage-count-datasets" />
        <Kpi icon={ShieldCheck} label="Eazy-managed sources" value={num(data.counts?.managed_sources)} testid="usage-count-managed" />
        <Kpi icon={Activity} label="Files uploaded" value={num(st.media_files)} sub={fmtBytes(st.media_bytes)} testid="usage-count-media" />
      </div>
    </div>
  );
}
